import { ServiceRequest, Tag } from 'src/models';
import {
  formatStaticDate,
  getPractitionerName,
  generateColorFromName,
} from 'src/utils';
import { Avatar, Tooltip, Dropdown, Button } from 'antd';
import { format } from 'date-fns';
import {
  TagsOutlined,
  DeleteOutlined,
  SendOutlined,
  UserOutlined,
  LoadingOutlined,
  CheckOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { ServiceRequestStatus, TagSelector, InsightTag } from 'src/components';
import { ChatCompletionStatus } from 'src/components/serviceRequest/serviceRequestChatCompletionStatus';
import {
  type TableProps,
  Modal,
  toast,
} from '@insight-health-ai/roseate/components';
import {
  MessageTextSquare01,
  DotsVertical,
  MessageNotificationSquare,
} from '@insight-health-ai/roseate/icons';
import {
  useDeleteServiceRequest,
  useSendServiceRequestReminder,
  UseGetServiceRequestListParams,
  useMarkServiceRequestProcessed,
} from 'src/hooks/serviceRequest';
import { logger } from 'src/services';
import type { MenuProps } from 'antd';

export const sortDirectionMap = {
  ascend: 'asc',
  descend: 'desc',
} as const;

export const defaultFilters = {
  tagIds: [],
  sortBy: 'created_at',
  sortDirection: 'desc',
} satisfies UseGetServiceRequestListParams;

const RowActions = ({
  record,
  onViewNotificationHistory,
}: {
  record: ServiceRequest;
  onViewNotificationHistory: (record: ServiceRequest) => void;
}) => {
  const { mutateAsync: sendReminder, isPending: isSendingReminder } =
    useSendServiceRequestReminder();
  const {
    mutateAsync: deleteServiceRequest,
    isPending: isDeletingServiceRequest,
  } = useDeleteServiceRequest();

  const {
    mutateAsync: markServiceRequestProcessed,
    isPending: isMarkingServiceRequestProcessed,
  } = useMarkServiceRequestProcessed();

  const onSendReminder = async () => {
    await sendReminder({ serviceRequestId: record.service_request_id });
    toast.success('Reminder sent successfully');
  };

  const onMarkServiceRequestProcessed = async () => {
    await markServiceRequestProcessed({
      serviceRequestId: record.service_request_id,
      is_processed: true,
    });
    toast.success(`Marked as processed`);
  };

  const onMarkServiceRequestUnprocessed = async () => {
    await markServiceRequestProcessed({
      serviceRequestId: record.service_request_id,
      is_processed: false,
    });
    toast.success(`Reverted back to ${record.type.initial_status}`);
  };

  const onViewNotificationHistoryHandler = () => {
    onViewNotificationHistory(record);
  };

  const onDelete = async () => {
    Modal.confirm({
      appearance: 'error',
      title: 'Delete Referral',
      content: 'Are you sure you want to delete this referral?',
      primaryAction: {
        onClick: async () => {
          await deleteServiceRequest({
            serviceRequestId: record.service_request_id,
          });
          toast.success('Referral deleted successfully');
        },
        label: 'Delete',
      },
    });
  };

  const actions = [] as Exclude<MenuProps['items'], undefined>;

  if (
    (record.encounter?.chat_completion_status || '').toLowerCase() !==
    'undeliverable'
  ) {
    if (
      ['new', 'inprogress'].includes(
        (record.encounter?.chat_completion_status || '').toLowerCase()
      )
    ) {
      actions.push({
        key: 'send-patient-reminder',
        label: 'Send patient reminder',
        onClick: onSendReminder,
        disabled: isSendingReminder,
        icon: isSendingReminder ? <LoadingOutlined /> : <SendOutlined />,
      });
    }

    if (record.encounter?.chat_url) {
      actions.push({
        key: 'open-chat-as-patient',
        label: 'Open chat as patient',
        onClick: () => {
          window.open(
            record.encounter?.chat_url,
            '_blank',
            'noopener,noreferrer'
          );
        },
        icon: <MessageTextSquare01 />,
      });
    }
  }

  actions.push({
    key: 'delete-referral',
    label: 'Delete referral',
    onClick: onDelete,
    disabled: isDeletingServiceRequest,
    icon: isDeletingServiceRequest ? <LoadingOutlined /> : <DeleteOutlined />,
  });

  if (record.status?.status_name?.toLowerCase() === 'processed') {
    actions.push({
      key: 'mark-as-unprocessed',
      label: `Revert back to ${record.type.initial_status}`,
      onClick: onMarkServiceRequestUnprocessed,
      disabled: isMarkingServiceRequestProcessed,
      icon: isMarkingServiceRequestProcessed ? (
        <LoadingOutlined />
      ) : (
        <ReloadOutlined />
      ),
    });
  } else {
    actions.push({
      key: 'mark-as-processed',
      label: 'Mark as processed',
      onClick: onMarkServiceRequestProcessed,
      disabled: isMarkingServiceRequestProcessed,
      icon: isMarkingServiceRequestProcessed ? (
        <LoadingOutlined />
      ) : (
        <CheckOutlined />
      ),
    });
  }

  if (
    !['fax-referral'].includes(record.type?.service_request_key?.toLowerCase())
  ) {
    // Fax referrals don't have a notification history
    actions.push({
      key: 'view-notification-history',
      label: 'View notification history',
      onClick: onViewNotificationHistoryHandler,
      disabled: false,
      icon: <MessageNotificationSquare />,
    });
  }

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: actions,
      }}
    >
      <Button type="text" icon={<DotsVertical />} />
    </Dropdown>
  );
};

export const getReferralListColumns = ({
  filters,
  onTagChange,
  isSuperAdmin,
  onViewNotificationHistory,
}: {
  filters: UseGetServiceRequestListParams;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  onTagChange: (
    record: ServiceRequest,
    tags: Tag,
    action: 'add' | 'remove'
  ) => void;
  onViewNotificationHistory: (record: ServiceRequest) => void;
}) =>
  [
    {
      title: 'Practitioner',
      dataIndex: 'performing_practitioner',
      key: 'practitioner_name',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (practitioner: ServiceRequest['performing_practitioner']) => {
        if (!practitioner) {
          return <div className="text-gray-500 italic">Unknown</div>;
        }

        const { first_name, last_name } = practitioner;

        const name = getPractitionerName(practitioner) || '';
        const nameParts = [first_name, last_name];
        const initials = nameParts
          .map((part) => part?.[0])
          .join('')
          .toUpperCase();
        return (
          <div className="flex items-center gap-2">
            <Avatar
              style={{
                flexShrink: 0,
                color: 'white',
                backgroundColor: generateColorFromName(name),
              }}
            >
              {initials}
            </Avatar>
            {name}
          </div>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (
        created_at: ServiceRequest['created_at'],
        { requesting_practitioner }: ServiceRequest
      ) => {
        return (
          <div className="flex flex-col gap-1">
            <div>{format(new Date(created_at), 'MMMM do, yyyy')}</div>
            {requesting_practitioner &&
              (() => {
                const name = getPractitionerName(requesting_practitioner);
                return (
                  <div className="flex items-center gap-1 text-xs text-gray-500 text-xs">
                    <span className="text-gray-500">From</span>
                    <span className="flex items-center gap-1 font-medium text-nowrap">
                      <UserOutlined />
                      <span>{name}</span>
                    </span>
                  </div>
                );
              })()}
          </div>
        );
      },
    },
    {
      title: 'Patient',
      dataIndex: 'patient',
      key: 'patient',
      render: (patient: ServiceRequest['patient']) => {
        if (!patient) {
          return <div className="text-gray-500 italic">Unknown</div>;
        }

        return (
          <div className="flex flex-col gap-1">
            <div className="font-medium flex items-center gap-2">
              {[patient.first_name, patient.last_name]
                .filter(Boolean)
                .join(' ')}
            </div>
            <div className="text-xs text-gray-500">
              {formatStaticDate(patient.birth_date)}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Chat Status',
      dataIndex: 'encounter',
      key: 'chat_completed_at',
      onCell: () =>
        isSuperAdmin
          ? {
              onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
                e.stopPropagation();
              },
              style: {
                cursor: 'default',
              },
            }
          : {},
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (
        encounter: ServiceRequest['encounter'],
        record: ServiceRequest
      ) => {
        if (!encounter) {
          return '';
        }

        const { chat_completion_status, chat_completed_at, chat_url } =
          encounter;

        const renderChatLink = () => {
          if (!isSuperAdmin) {
            return null;
          }

          let chatUrl = null;

          if (chat_url) {
            try {
              chatUrl = new URL(chat_url);
              chatUrl.searchParams.set('from_care_connect', 'true');
            } catch (error) {
              logger.warn('Invalid chat URL on referral service request', {
                chat_url,
                encounterId: encounter.encounter_id,
                serviceRequestId: record.service_request_id,
              });
            }
          }

          if (!chatUrl) {
            return null;
          }

          return (
            <Tooltip title="Open chat as practitioner">
              <a
                href={chatUrl.toString()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageTextSquare01 className="text-lg hover:text-primary" />
              </a>
            </Tooltip>
          );
        };

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <ChatCompletionStatus status={chat_completion_status} />
              {renderChatLink()}
            </div>
            {chat_completed_at && (
              <div className="text-xs text-gray-500">
                {format(new Date(chat_completed_at), 'MMMM do, yyyy hh:mm a')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: ServiceRequest['type']) => {
        if (!type) {
          return '';
        }

        return <div>{type.service_request_type}</div>;
      },
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      onCell: () => ({
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
          e.stopPropagation();
        },
        style: {
          cursor: 'default',
          maxWidth: 250,
        },
      }),
      render: (tags: ServiceRequest['tags'], record: ServiceRequest) => {
        if (!tags) {
          return '';
        }

        const wrapList = tags.length > 2;

        const tagsToRender = wrapList ? tags.slice(0, 2) : tags;

        let wrappedJsx = null;
        if (wrapList) {
          wrappedJsx = (
            <Tooltip
              title={
                <div className="flex flex-col gap-4 p-2 max-w-[300px]">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <TagsOutlined /> {tags.length} Tags
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags?.map((tag) => (
                      <InsightTag
                        key={tag.id}
                        tag={tag}
                        isSelected
                        onClose={() => {
                          onTagChange(record, tag, 'remove');
                        }}
                      />
                    ))}
                  </div>
                </div>
              }
            >
              <span className="text-sm">
                +{tags.length - tagsToRender.length}
              </span>
            </Tooltip>
          );
        }

        return (
          <div className="flex gap-2 flex-wrap">
            {tagsToRender?.map((tag) => (
              <InsightTag
                key={tag.id}
                tag={tag}
                isSelected
                onClose={() => {
                  onTagChange(record, tag, 'remove');
                }}
              />
            ))}
            {wrappedJsx}
            <TagSelector
              selectedTags={tags}
              onAdd={(tag) => {
                onTagChange(record, tag, 'add');
              }}
              onRemove={(tag) => {
                onTagChange(record, tag, 'remove');
              }}
            />
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ServiceRequest['status']) => {
        if (!status) {
          return '';
        }

        return (
          <ServiceRequestStatus status={status} statusProps={{ size: 'sm' }} />
        );
      },
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right' as const,
      onCell: () => ({
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
          e.stopPropagation();
        },
        style: {
          cursor: 'default',
          width: 50,
        },
      }),
      render: (_: unknown, record: ServiceRequest) => {
        return (
          <RowActions
            record={record}
            onViewNotificationHistory={onViewNotificationHistory}
          />
        );
      },
    },
  ].map((column) => {
    const columnObj = {
      ...column,
      ...(column.key === filters.sortBy && {
        defaultSortOrder:
          filters.sortDirection === 'desc' ? 'descend' : 'ascend',
      }),
    };

    return columnObj;
  }) as TableProps<ServiceRequest>['columns'];
